import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  StateControlParameters,
  StateService,
  ZaverProduct,
} from '../state/state.service';
import { QueryParamsService } from '../query-params/query-params.service';
import {
  Market,
  PaymentMethod,
  PaymentRequestDto,
  PaymentRequestStatus,
} from '@app/model/payment-request/payment-request.dto';
import { Router } from '@angular/router';
import { routeNames } from '@assets/val/route-constants';
import { LoggingService } from '../logging-service/logging.service';

@Injectable({
  providedIn: 'root',
})
export class StateSyncService implements OnDestroy {
  visibilityHandler = () => this.onVisibilityChange();
  isInitialized: boolean = false;

  constructor(
    protected router: Router,
    private ngZone: NgZone,
    private stateService: StateService,
    private queryParamsService: QueryParamsService,
    private loggingService: LoggingService
  ) {}

  initialize(): void {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    });
  }

  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      // On returning to a previously opened checkout tab:
      this.ngZone.run(() => {
        this.navigateToSuccessOnSettled();
      });
    }
  }

  async navigateToSuccessOnSettled(): Promise<void> {
    const token =
      this.stateService.getToken() ??
      this.queryParamsService.getQueryParams().id;

    if (!token) {
      return;
    }

    if (this.stateService.getProductStateType() === ZaverProduct.PRECHECK) {
      // If is a precheck and therefore not a payment request, don't attempt to state sync.
      return;
    }

    this.loggingService.warn(
      `Tab revisit detected, attempting to sync FE checkout state...`
    );

    const paymentRequest: PaymentRequestDto =
      await this.stateService.fetchPaymentRequest(token);

    if (paymentRequest.status === PaymentRequestStatus.SETTLED) {
      // FE driven flow SE
      if (
        paymentRequest.market === Market.SE &&
        paymentRequest.settlementMethod !== PaymentMethod.PAY_IN_ADVANCE // PIA is backend driven
      ) {
        const isBackendDriven =
          await this.stateService.checkUseBackendDrivenFlow(
            paymentRequest.settlementMethod,
            token
          );
        if (!isBackendDriven) {
          this.router.navigate([routeNames.SUCCESS], {
            queryParamsHandling: 'preserve',
          });
        }
      }

      // Commented out to fix race condition where state and register would be called...
      // else {
      //   // BE driven flows
      //   const path: string = window.location.pathname.slice(1); // Removes first '/' from path name.

      //   try {
      //     const controlParams: StateControlParameters = {
      //       currentPageName: this.stateService.getStepFromUrl(path),
      //       token: token,
      //     };

      //     let updatedState = await this.stateService.fetchState(controlParams);

      //     if (updatedState.forceNavigation) {
      //       const route = this.stateService.getRouteFromState(updatedState);
      //       this.router.navigate([route], { queryParamsHandling: 'preserve' });
      //     }
      //   } catch (err) {
      //     this.loggingService.warn(
      //       `Could not fetch state from path: /${path} with token: ${token}`
      //     );
      //   }
      // }
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }
}
